import { CheckCircle2, Star, AlertTriangle } from "lucide-react";

import type { PresetItem } from "@/entities/projects/types";
import { cn } from "@/shared/lib/cn";
import { formatNumber } from "@/shared/lib/format";

export interface PresetCardProps {
  name: "economy" | "standard" | "deep";
  item: PresetItem;
  selected: boolean;
  onSelect: () => void;
}

const NAME_LABEL: Record<PresetCardProps["name"], string> = {
  economy: "Эконом",
  standard: "Стандарт",
  deep: "Глубокий",
};

const NAME_HINT: Record<PresetCardProps["name"], string> = {
  economy: "Быстрый прогон",
  standard: "Большинству — это",
  deep: "Полное покрытие",
};

export function PresetCard({
  name,
  item,
  selected,
  onSelect,
}: PresetCardProps): JSX.Element {
  const e = item.estimate;
  const minutes =
    e.estimated_minutes_max === e.estimated_minutes_min
      ? `~${e.estimated_minutes_min} мин`
      : `до ${e.estimated_minutes_max} мин`;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative flex flex-col gap-3 rounded-2xl border p-5 text-left transition-all",
        selected
          ? "border-purple/60 bg-gradient-to-br from-purple/[0.14] to-cyan/[0.06] shadow-glow-purple"
          : "border-white/10 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]",
      )}
    >
      <div className="flex items-baseline justify-between">
        <div className="flex items-baseline gap-2">
          <h3 className="text-base font-bold text-white">{NAME_LABEL[name]}</h3>
          {name === "standard" && (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber">
              <Star className="h-3 w-3" /> рекоменд.
            </span>
          )}
          {e.will_hit_quota && (
            <span className="inline-flex items-center gap-1 rounded-md bg-pink/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-pink">
              <AlertTriangle className="h-3 w-3" /> упрётся в квоту
            </span>
          )}
        </div>
        {selected && <CheckCircle2 className="h-5 w-5 text-purple" />}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            фраз
          </div>
          <div className="font-mono text-base font-bold text-white">
            {formatNumber(item.params.target_phrases)}
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            стоимость
          </div>
          <div className="font-mono text-base font-bold text-cyan">
            {e.cost_rub.toFixed(2)} ₽
          </div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            время
          </div>
          <div className="font-mono text-base font-bold text-white">{minutes}</div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">{NAME_HINT[name]}</div>
    </button>
  );
}
